/* =====================================================================
   CLADDING MATHS — pure functions only. No DOM, no state, no side effects.
   Lengths in millimetres, money in pence (inc VAT). Runs in a browser or in Node.
   Built from the approved mockup's CladdingMaths (slatted-pdp-draft-12.html), unchanged except where noted "Build:".
   Build: no price table. Every price comes in with the job (quote(job, P)), read by the page from the live
   products' own variants, wired by variant ID. A part with no price is not made in that length or colour: it is
   left out of the plan, or the length that lacks it is not offered, never priced from somewhere else.
   Rules: blueprint 4.8 as amended by Daniel's answers of 15 Oct 2026 (section 0):
   - boards counted column by column (vertical) or row by row (horizontal), cut pieces packed
     into whole boards first-fit, longest first; shortest piece in a joined run 1.0 m
   - spares: 5% on the fitted count, rounded up to whole boards (or exact count)
   - battens at 500 mm, one layer, no counter-battens
   - screws only: one Screw Pack per 10 fitted boards; colour-coded screws for the first and
     last boards and the face-fixed trims (packs of 50)
   - corner pieces can never meet another corner piece (Daniel, 16 Oct 2026): a corner piece,
     external or internal, only where two clad walls meet in a single L and neither wall has
     another corner (see hasCorner and singleL); every other corner, outside or inside, gets
     edging trim; horizontal boards always take edging at an outside corner
   - delivery inside the total: the charge on any order up to the free-delivery figure, free only over it
     (Build: Daniel, 19 Oct 2026: "free over £500"; an order of exactly £500 still pays)
   ===================================================================== */
var CladdingMaths = (function () {
  'use strict';

  var COVER = 200;          // each board adds 200 mm to the wall
  var BAY = 500;            // batten centres
  var MIN_PIECE = 1000;     // shortest piece in a joined run (spans three battens)
  var STEP = 100;           // cut positions tried every 100 mm
  var SCREW_SPACING = 300;  // colour-coded screws along a face-fixed trim
  var SPARES_PCT = 5;

  /* Build: prices travel with each job as P = { board: {2500, 3600}, corner: {2500, 3600}, internal: {2500, 3600},
     edging: {2500, 3600}, ftrim: {3600}, batten: {3000, 3600}, screwPack, colourScrewPack } in pence, plus the
     delivery terms D = { delivery, freeOver } in pence. A missing entry means that part is not sold in that length
     or colour. */
  function has(o, k) { return !!o && o[k] != null; }

  function sum(a) { var s = 0; for (var i = 0; i < a.length; i++) s += a[i]; return s; }
  function desc(a, b) { return b - a; }

  /* First-fit decreasing: pack pieces into whole lengths. An offcut is reused only when a piece fits. */
  function ffd(pieces, length) {
    var bins = [];
    var sorted = pieces.slice().sort(desc);
    for (var i = 0; i < sorted.length; i++) {
      var p = sorted[i], placed = false;
      for (var j = 0; j < bins.length; j++) {
        if (bins[j].free >= p) { bins[j].free -= p; bins[j].pieces.push(p); placed = true; break; }
      }
      if (!placed) bins.push({ free: length - p, pieces: [p] });
    }
    return { count: bins.length, bins: bins };
  }

  /* Ways to cut one run (a row, a column or a batten line) into pieces <= L, each >= MIN_PIECE.
     twoOnly: a vertical column taller than a board is always two pieces with one join on a double
     batten (blueprint 4.8), so three-piece splits are not tried for it. Rows and batten lines may
     take three pieces when that packs into fewer lengths. */
  function splitOptions(S, L, twoOnly) {
    if (S <= L) return [[S]];
    var out = {}, k = Math.ceil(S / L), a, b, c;
    function add(arr) {
      for (var i = 0; i < arr.length; i++) if (arr[i] < MIN_PIECE || arr[i] > L) return;
      var s = arr.slice().sort(desc); out[s.join(',')] = s;
    }
    if (k === 2) {
      for (a = MIN_PIECE; a <= L; a += STEP) { b = S - a; add([a, b]); }
      if (!twoOnly) for (a = MIN_PIECE; a <= L; a += STEP) for (b = MIN_PIECE; b <= L; b += STEP) { c = S - a - b; add([a, b, c]); }
    } else {
      if (twoOnly) return []; // a column needing three pieces: this board length can't build it
      [k, k + 1].forEach(function (n) {
        var base = Math.floor(S / n / STEP) * STEP, eq = [];
        for (var i = 0; i < n - 1; i++) eq.push(base);
        eq.push(S - base * (n - 1)); add(eq);
        var full = [], rem = S;
        while (rem > L) { full.push(L); rem -= L; }
        if (rem < MIN_PIECE && full.length) { var take = MIN_PIECE - rem; full[full.length - 1] -= take; rem += take; }
        full.push(rem); if (full.length === n) add(full);
      });
    }
    var list = Object.keys(out).map(function (key) { return out[key]; });
    if (!list.length) { // last resort: equal pieces
      var n2 = k + 1, base2 = Math.ceil(S / n2), arr = [];
      for (var i2 = 0; i2 < n2 - 1; i2++) arr.push(base2);
      arr.push(S - base2 * (n2 - 1)); list.push(arr);
    }
    return list;
  }

  /* Pack many runs into stock lengths L. Each distinct run length gets the split that packs best,
     then every piece of the job is packed together, so offcuts are shared across walls. */
  function packRuns(runs, L, twoOnly) {
    var groups = {}, pattern = {}, pieces = [], buildable = true;
    runs.forEach(function (r) { groups[r] = (groups[r] || 0) + 1; });
    Object.keys(groups).forEach(function (key) {
      var S = +key, n = groups[key], opts = splitOptions(S, L, twoOnly);
      if (!opts.length) { buildable = false; pattern[S] = [S]; return; }
      if (opts.length === 1) { pattern[S] = opts[0]; return; }
      var best = null;
      opts.forEach(function (o) {
        var ps = [];
        for (var i = 0; i < n; i++) ps = ps.concat(o);
        var cnt = ffd(ps, L).count;
        if (!best || cnt < best.cnt || (cnt === best.cnt && o.length < best.o.length)) best = { cnt: cnt, o: o };
      });
      pattern[S] = best.o;
    });
    runs.forEach(function (r) { pieces = pieces.concat(pattern[r]); });
    var packed = ffd(pieces, L);
    return { count: packed.count, pattern: pattern, pieces: pieces, bins: packed.bins, buildable: buildable };
  }

  /* Trims join anywhere (2 mm gap), so a long edge is whole lengths plus one remainder. */
  function trimPieces(run, T) {
    var out = [];
    while (run > T) { out.push(T); run -= T; }
    if (run > 0) out.push(run);
    return out;
  }
  function packTrim(runs, T) {
    var ps = [];
    runs.forEach(function (r) { ps = ps.concat(trimPieces(r, T)); });
    return ffd(ps, T).count;
  }

  /* ---------- Wall geometry ---------- */
  function heightAt(wall, x) {
    if (!wall.h2 || wall.h2 === wall.h) return wall.h;
    return wall.h + (wall.h2 - wall.h) * (x / wall.w);
  }
  function hMax(wall) { return Math.max(wall.h, wall.h2 || wall.h); }
  function hMin(wall) { return Math.min(wall.h, wall.h2 || wall.h); }
  function topLength(wall) { var d = (wall.h2 || wall.h) - wall.h; return Math.round(Math.sqrt(wall.w * wall.w + d * d)); }

  /* Default positions: openings spread along the wall, snapped to the 200 mm board grid. */
  function placeOpenings(wall) {
    var n = (wall.openings || []).length;
    return (wall.openings || []).map(function (o, i) {
      var centre = wall.w * (i + 1) / (n + 1);
      var ox = Math.round((centre - o.w / 2) / COVER) * COVER;
      ox = Math.max(0, Math.min(wall.w - o.w, ox));
      var oy = o.type === 'window' ? (o.sill || 0) : 0;
      return { type: o.type, w: o.w, h: o.h, sill: o.sill || 0, x: ox, y: oy };
    });
  }

  function netArea(walls) {
    var a = 0;
    walls.forEach(function (w) {
      a += w.w * (w.h + (w.h2 || w.h)) / 2;
      (w.openings || []).forEach(function (o) { a -= o.w * o.h; });
    });
    return a / 1e6; // m²
  }

  /* Subtract covered intervals from [a, b]. */
  function subtract(a, b, cuts) {
    var segs = [[a, b]];
    cuts.forEach(function (c) {
      var next = [];
      segs.forEach(function (s) {
        if (c[1] <= s[0] || c[0] >= s[1]) { next.push(s); return; }
        if (c[0] > s[0]) next.push([s[0], c[0]]);
        if (c[1] < s[1]) next.push([c[1], s[1]]);
      });
      segs = next;
    });
    return segs.filter(function (s) { return s[1] - s[0] > 0; });
  }

  /* Strips (columns or rows) of one wall, each with the segments that need board. */
  function wallStrips(wall, dir) {
    var ops = placeOpenings(wall), strips = [];
    if (dir === 'vertical') {
      var cols = Math.ceil(wall.w / COVER);
      for (var c = 0; c < cols; c++) {
        var x0 = c * COVER, x1 = Math.min(wall.w, x0 + COVER);
        var Hc = Math.round(Math.max(heightAt(wall, x0), heightAt(wall, x1)));
        var cuts = ops.filter(function (o) { return o.x <= x0 && x1 <= o.x + o.w; })
          .map(function (o) { return [o.y, o.y + o.h]; });
        strips.push({ a: x0, b: x1, len: Hc, segs: subtract(0, Hc, cuts) });
      }
    } else {
      var H = hMax(wall), Hl = hMin(wall), rows = Math.ceil(H / COVER);
      for (var r = 0; r < rows; r++) {
        var y0 = r * COVER, y1 = Math.min(H, y0 + COVER);
        var width = y0 <= Hl ? wall.w : Math.round(wall.w * (H - y0) / (H - Hl));
        var xStart = 0, xEnd = width;
        if (y0 > Hl && (wall.h2 || wall.h) < wall.h) { xStart = 0; xEnd = width; }
        else if (y0 > Hl) { xStart = wall.w - width; xEnd = wall.w; }
        var rcuts = ops.filter(function (o) { return o.y <= y0 && y1 <= o.y + o.h; })
          .map(function (o) { return [o.x, o.x + o.w]; });
        strips.push({ a: y0, b: y1, len: width, x0: xStart, segs: subtract(xStart, xEnd, rcuts) });
      }
    }
    return strips;
  }

  /* Boards for the whole job at one board length. */
  function countBoards(walls, dir, L) {
    var runs = [], perWall = [];
    walls.forEach(function (w) {
      var strips = wallStrips(w, dir);
      strips.forEach(function (s) { s.segs.forEach(function (g) { runs.push(Math.round(g[1] - g[0])); }); });
      perWall.push({ strips: strips });
    });
    var packed = packRuns(runs, L, dir === 'vertical');
    // joins: per wall, the most joins any strip carries, and the vertical join heights
    var maxJoins = 0;
    perWall.forEach(function (pw) {
      pw.maxJoins = 0;
      pw.strips.forEach(function (s) {
        s.segs.forEach(function (g) {
          var n = packed.pattern[Math.round(g[1] - g[0])].length - 1;
          if (n > pw.maxJoins) pw.maxJoins = n;
        });
      });
      if (pw.maxJoins > maxJoins) maxJoins = pw.maxJoins;
    });
    return { L: L, fitted: packed.count, pattern: packed.pattern, pieces: packed.pieces, bins: packed.bins, perWall: perWall, maxJoins: maxJoins, buildable: packed.buildable };
  }

  function sparesFor(fitted, on) { return on ? Math.ceil(fitted * (100 + SPARES_PCT) / 100) - fitted : 0; }

  /* Build: the lengths this range and colour are sold in (Fire Rated: 3.6 m only), from its live variants. */
  function boardLengths(P) { return [2500, 3600].filter(function (L) { return has(P.board, L); }); }

  /* Cost the job at every length the range comes in; the cheaper buildable plan wins. */
  function chooseBoards(walls, dir, P, sparesOn) {
    var lens = boardLengths(P);
    if (!lens.length) return null; // Build: no board found for this range and colour
    var opts = lens.map(function (L) {
      var b = countBoards(walls, dir, L);
      b.spares = sparesFor(b.fitted, sparesOn);
      b.ordered = b.fitted + b.spares;
      b.unit = P.board[L];
      b.cost = b.ordered * b.unit;
      b.costFitted = b.fitted * b.unit;
      return b;
    });
    // a length that would need two joins in a vertical column is not offered
    var ok = opts.filter(function (o) { return o.buildable; });
    if (!ok.length) ok = opts;
    var sorted = ok.slice().sort(function (a, b) { return a.cost - b.cost || a.maxJoins - b.maxJoins || b.L - a.L; });
    return { best: sorted[0], alt: sorted[1] || null, dropped: opts.filter(function (o) { return ok.indexOf(o) < 0; }).map(function (o) { return o.L; }) };
  }

  /* ---------- Sides, corners and trims ---------- */
  // Side of wall i: {kind:'end'|'joint', value, index}
  function sideOf(job, i, which) {
    var n = job.walls.length, closed = job.closed && n >= 3;
    if (which === 'left') {
      if (i === 0 && !closed) return { kind: 'end', value: job.ends.left };
      return { kind: 'joint', value: job.joints[(i - 1 + n) % n], index: (i - 1 + n) % n };
    }
    if (i === n - 1 && !closed) return { kind: 'end', value: job.ends.right };
    return { kind: 'joint', value: job.joints[i], index: i };
  }
  /* Does this side of a wall make a corner? Every joint with another wall in the plan does, outside or
     inside, and so does an end that turns the corner or meets a wall coming in. */
  function hasCorner(side) { return side.kind === 'joint' || side.value === 'corner' || side.value === 'internal'; }
  /* Joint j is a single L when neither of the two walls it joins has a corner at its other side.
     Only a single L takes a corner piece, external or internal (Daniel, 16 Oct 2026). */
  function singleL(job, j) {
    var n = job.walls.length;
    return !hasCorner(sideOf(job, j, 'left')) && !hasCorner(sideOf(job, (j + 1) % n, 'right'));
  }
  function jointCount(job) { var n = job.walls.length; return job.closed && n >= 3 ? n : n - 1; }

  var WHY = {
    notSingleL: 'This corner gets edging trim: a corner piece only works on a single L, where neither wall has another corner.',
    horizontal: 'This corner gets edging trim: horizontal board ends can’t slot into a corner piece.',
    offPlan: 'This corner gets edging trim: the corner piece only works where two walls in your plan meet in a single L.',
    insideNotSingleL: 'This inside corner gets edging trim: an internal corner piece only works on a single L, where neither wall has another corner.',
    insideOffPlan: 'This corner gets edging trim: an internal corner piece only works where two walls in your plan meet in a single L.'
  };

  function planTrims(job, dir) {
    var n = job.walls.length, walls = job.walls;
    var corners = [], internals = [], edging = [], fPieces = [], sides = [];
    var J = jointCount(job);
    for (var j = 0; j < J; j++) {
      var a = j, b = (j + 1) % n;
      var h = Math.round(Math.max(heightAt(walls[a], walls[a].w), heightAt(walls[b], 0)));
      var type = job.joints[j], single = singleL(job, j);
      if (type === 'in') {
        if (single) {
          internals.push({ h: h, where: 'joint', index: j });
          sides.push({ key: 'j' + j, kind: 'joint', index: j, value: type, trim: 'internal', h: h });
        } else { // an inside corner that isn't a single L (an alcove, say) is capped with edging trim
          edging.push({ len: h, where: 'corner', inside: true, index: j });
          sides.push({ key: 'j' + j, kind: 'joint', index: j, value: type, trim: 'edging', h: h, why: WHY.insideNotSingleL });
        }
      } else {
        if (dir === 'vertical' && single) {
          corners.push({ h: h, index: j });
          sides.push({ key: 'j' + j, kind: 'joint', index: j, value: type, trim: 'corner', h: h });
        } else {
          edging.push({ len: h, where: 'corner', index: j });
          sides.push({ key: 'j' + j, kind: 'joint', index: j, value: type, trim: 'edging', h: h, why: dir === 'horizontal' ? WHY.horizontal : WHY.notSingleL });
        }
      }
    }
    if (!(job.closed && n >= 3)) {
      [['left', walls[0], 0], ['right', walls[n - 1], walls[n - 1].w]].forEach(function (e) {
        var v = job.ends[e[0]], h2 = Math.round(heightAt(e[1], e[2])), s = { key: e[0], kind: 'end', value: v, h: h2 };
        // an end that turns the corner or meets a wall coming in has only one clad wall in the plan: edging trim
        if (v === 'frame' && dir === 'horizontal') { fPieces.push(h2); s.trim = 'ftrim'; }
        else {
          edging.push({ len: h2, where: e[0], turns: v === 'corner', meets: v === 'internal' }); s.trim = 'edging';
          if (v === 'corner') s.why = dir === 'horizontal' ? WHY.horizontal : WHY.offPlan;
          if (v === 'internal') s.why = WHY.insideOffPlan;
        }
        sides.push(s);
      });
    }
    var topRuns = [], baseRuns = [];
    walls.forEach(function (w) {
      if (job.top === 'open') { edging.push({ len: topLength(w), where: 'top' }); topRuns.push(topLength(w)); }
      if (job.bottom === 'edging') {
        var doors = sum(placeOpenings(w).filter(function (o) { return o.type === 'door'; }).map(function (o) { return o.w; }));
        var base = w.w - doors;
        if (base > 0) { edging.push({ len: base, where: 'base' }); baseRuns.push(base); }
      }
      placeOpenings(w).forEach(function (o) {
        if (o.type === 'window') fPieces.push(o.w, o.w, o.h, o.h);
        else fPieces.push(o.w, o.h, o.h);
      });
    });
    sides.push({ key: 'top', kind: 'top', value: job.top, trim: job.top === 'open' ? 'edging' : null });
    sides.push({ key: 'bottom', kind: 'bottom', value: job.bottom, trim: job.bottom === 'edging' ? 'edging' : null });
    return { corners: corners, internals: internals, edging: edging, fPieces: fPieces, sides: sides, topRuns: topRuns, baseRuns: baseRuns };
  }

  function trimCost(plan, T, P) {
    var cornerN = sum(plan.corners.map(function (c) { return Math.ceil(c.h / T); }));
    var internalN = sum(plan.internals.map(function (c) { return Math.ceil(c.h / T); }));
    var edgingN = packTrim(plan.edging.map(function (e) { return e.len; }), T);
    // Build: a trim length is only on offer when every trim the job needs is sold at it in the trim colour
    var ok = (!cornerN || has(P.corner, T)) && (!internalN || has(P.internal, T)) && (!edgingN || has(P.edging, T));
    return {
      T: T, cornerN: cornerN, internalN: internalN, edgingN: edgingN, ok: ok,
      cost: ok ? cornerN * (cornerN ? P.corner[T] : 0) + internalN * (internalN ? P.internal[T] : 0) + edgingN * (edgingN ? P.edging[T] : 0) : Infinity
    };
  }

  /* One trim length for the whole job (2.5 m and 3.6 m profiles must not mix). */
  function chooseTrims(plan, trimColour, P) {
    var a = trimCost(plan, 2500, P), b = trimCost(plan, 3600, P), pick, why;
    var fN = plan.fPieces.length ? ffd([].concat.apply([], plan.fPieces.map(function (p) { return trimPieces(p, 3600); })), 3600).count : 0;
    var any = a.cornerN + a.internalN + a.edgingN;
    if (!any) { pick = b; why = ''; }
    else if (fN) { pick = b; why = 'All trims in 3.6 m: your job has F trim, which only comes in 3.6 m, and trims of two lengths must not mix.'; }
    // Build: the mockup's "2.5 m corner piece isn't made in Natural Oak" rule, read from the live variants
    else if (!a.ok && b.ok) { pick = b; why = a.cornerN && !has(P.corner, 2500) ? 'All trims in 3.6 m: the 2.5 m corner piece isn’t made in ' + trimColour + '.' : 'All trims in 3.6 m: the 2.5 m trims aren’t made in ' + trimColour + '.'; }
    else if (a.ok && !b.ok) { pick = a; why = 'All trims in 2.5 m: the 3.6 m trims aren’t made in ' + trimColour + '.'; }
    else if (a.cost < b.cost) { pick = a; why = 'All trims in 2.5 m: they cost less for this job (' + money(a.cost) + ' against ' + money(b.cost) + ' in 3.6 m).'; }
    else { pick = b; why = 'All trims in 3.6 m: they cost ' + (b.cost < a.cost ? 'less for this job (' + money(b.cost) + ' against ' + money(a.cost) + ' in 2.5 m)' : 'the same and need fewer joins') + '.'; }
    pick.fN = fN; pick.why = why;
    pick.unavailable = any > 0 && !pick.ok; // Build: neither length sold in this colour: the trims are left out
    pick.fUnavailable = fN > 0 && !has(P.ftrim, 3600);
    pick.edgingLm = sum(plan.edging.map(function (e) { return e.len; }));
    pick.fLm = sum(plan.fPieces);
    return pick;
  }

  /* ---------- Battens: one layer at 500 mm centres, at 90 degrees to the boards ---------- */
  function battenLines(job, dir, boards, plan) {
    var lines = [], note = { rows: 0, edges: 0, buildouts: 0, joins: 0, openings: 0 };
    job.walls.forEach(function (w, i) {
      var H = hMax(w), pw = boards.perWall[i];
      if (dir === 'vertical') {
        var rows = Math.floor(H / BAY) + 2; // rows at 500 mm plus a top rail
        for (var r = 0; r < rows; r++) lines.push(w.w);
        note.rows += rows;
        // a vertical wall taller than a board gets a double batten along the join
        if (pw.maxJoins) { for (var k = 0; k < pw.maxJoins; k++) lines.push(w.w); note.joins += pw.maxJoins; }
      } else {
        var cols = Math.floor(w.w / BAY) + 1;
        for (var c = 0; c < cols; c++) lines.push(H);
        note.rows += cols;
        var extra = Math.min(cols, pw.maxJoins * (pw.strips.length > 1 ? 2 : 1));
        for (var e = 0; e < extra; e++) lines.push(H);
        note.joins += extra;
      }
      placeOpenings(w).forEach(function (o) {
        lines.push(o.h, o.h, o.w); note.openings += 3;
        if (o.type === 'window') { lines.push(o.w); note.openings += 1; }
      });
    });
    if (dir === 'vertical' && !(job.closed && job.walls.length >= 3)) {
      var n = job.walls.length;
      if (job.ends.left === 'open' || job.ends.left === 'frame') { lines.push(Math.round(heightAt(job.walls[0], 0))); note.edges++; }
      if (job.ends.right === 'open' || job.ends.right === 'frame') { lines.push(Math.round(heightAt(job.walls[n - 1], job.walls[n - 1].w))); note.edges++; }
    }
    plan.corners.forEach(function (c) { lines.push(c.h, c.h); note.buildouts += 2; });
    return { lines: lines, note: note, lm: sum(lines) };
  }

  function chooseBattens(lines, P) {
    var opts = [3000, 3600].filter(function (B) { return has(P.batten, B); }).map(function (B) {
      var p = packRuns(lines, B);
      return { B: B, count: p.count, unit: P.batten[B], cost: p.count * P.batten[B] };
    });
    if (!opts.length) return null; // Build: no batten found
    opts.sort(function (a, b) { return a.cost - b.cost; });
    opts[0].alt = opts[1] || null;
    return opts[0];
  }

  /* ---------- Screws ---------- */
  function colourScrews(job, dir, trims) {
    var face = Math.ceil(trims.edgingLm / SCREW_SPACING) + trims.edgingN;
    var firstLast = 0;
    job.walls.forEach(function (w) {
      firstLast += 2 * (dir === 'vertical' ? Math.floor(hMax(w) / BAY) + 2 : Math.floor(w.w / BAY) + 1);
    });
    var total = face + firstLast;
    return { face: face, firstLast: firstLast, total: total, packs: Math.ceil(total / 50) };
  }

  /* ---------- Money ---------- */
  function money(p) {
    var neg = p < 0; p = Math.abs(Math.round(p));
    var s = (p / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (neg ? '−' : '') + '£' + s;
  }
  function m(mm) { // 2400 -> "2.4 m", 3000 -> "3.0 m", 2450 -> "2.45 m"
    var v = mm / 1000, s = (Math.round(v * 100) / 100).toString();
    if (s.indexOf('.') === -1) s += '.0';
    return s + ' m';
  }
  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : (many || one + 's')); }

  /* Totals from (possibly edited) lines. Build: delivery is charged up to and including D.freeOver, free only over it
     (Daniel, 19 Oct 2026: an order of exactly £500 still pays £50). */
  function totals(lines, D) {
    var sub = sum(lines.map(function (l) { return l.qty * l.unit; }));
    var delivery = sub === 0 ? 0 : (sub > D.freeOver ? 0 : D.delivery);
    var total = sub + delivery;
    return { subtotal: sub, delivery: delivery, total: total, exVat: Math.round(total / 1.2), vat: total - Math.round(total / 1.2) };
  }

  /* ---------- The whole quote ---------- */
  function validJob(job) {
    if (!job.walls.length) return false;
    for (var i = 0; i < job.walls.length; i++) {
      var w = job.walls[i];
      if (!(w.w > 0 && w.h > 0)) return false;
      if (w.w > 30000 || w.h > 7200 || (w.h2 && w.h2 > 7200)) return false;
    }
    return true;
  }

  function quote(job, P, D) {
    if (!validJob(job)) return { valid: false };
    var dir = job.dir, range = job.range;
    var trimColour = range === 'duo' ? 'Midnight Black' : job.colour;
    var choice = chooseBoards(job.walls, dir, P, job.spares);
    if (!choice) return { valid: false, noBoards: true }; // Build: fail closed, the page shows the call-us note
    var boards = choice.best, alt = choice.alt;
    var plan = planTrims(job, dir);
    var trims = chooseTrims(plan, trimColour, P);
    var bat = battenLines(job, dir, boards, plan);
    var battens = job.fixing === 'ours' ? chooseBattens(bat.lines, P) : null;
    var missing = []; // Build: parts the plan needs that aren't sold in this length or colour
    if (job.fixing === 'ours' && !battens) missing.push('battens');
    if (trims.unavailable) missing.push('trims in ' + trimColour);
    if (trims.fUnavailable) missing.push('F trim in ' + trimColour);
    var screwPacks = Math.ceil(boards.fitted / 10);
    var cs = colourScrews(job, dir, trims);
    var area = netArea(job.walls);
    var T = trims.T, Tm = T === 2500 ? '2.5 m' : '3.6 m', Lm = boards.L === 2500 ? '2.5 m' : '3.6 m';
    var rangeName = range === 'signature' ? 'Signature' : range === 'fire' ? 'Fire Rated' : 'Duo Tone';
    var nW = job.walls.length, wallWord = nW === 1 ? 'the wall' : 'your ' + nW + ' walls';

    var lines = [];
    // Boards
    var bReason = boards.fitted + ' for ' + wallWord + (boards.spares ? ', plus ' + boards.spares + ' for cuts and spares (5%)' : ', no spares');
    lines.push({ id: 'boards', group: 'Boards', callout: null, name: 'Slatted board ' + Lm + ', ' + rangeName + ' ' + job.colour,
      qty: boards.ordered, unit: boards.unit, reason: bReason, part: { k: 'board-' + range, L: boards.L, c: job.colour } });
    // Trims
    if (trims.cornerN && !trims.unavailable) {
      lines.push({ id: 'corner', group: 'Trims', callout: 1, name: 'External corner piece ' + Tm + ', ' + trimColour, part: { k: 'corner', L: T, c: trimColour },
        qty: trims.cornerN, unit: P.corner[T], reason: plural(plan.corners.length, 'corner') + ' where two walls meet in a single L' + (trims.cornerN > plan.corners.length ? ', joined end to end' : '') });
    }
    if (trims.internalN && !trims.unavailable) {
      lines.push({ id: 'internal', group: 'Trims', callout: 2, name: 'Internal corner ' + Tm + ', ' + trimColour, part: { k: 'internal', L: T, c: trimColour },
        qty: trims.internalN, unit: P.internal[T], reason: plural(plan.internals.length, 'inside corner') + ' where two walls meet in a single L' + (trims.internalN > plan.internals.length ? ', joined end to end' : '') });
    }
    if (trims.edgingN && !trims.unavailable) {
      var parts = [];
      var nEnds = plan.edging.filter(function (e) { return (e.where === 'left' || e.where === 'right') && !e.turns && !e.meets; }).length;
      var nCorner = plan.edging.filter(function (e) { return (e.where === 'corner' && !e.inside) || e.turns; }).length;
      var nInside = plan.edging.filter(function (e) { return (e.where === 'corner' && e.inside) || e.meets; }).length;
      if (nEnds) parts.push(nEnds === 2 ? 'both ends' : 'one end');
      if (nCorner) parts.push(plural(nCorner, 'outside corner'));
      if (nInside) parts.push(plural(nInside, 'inside corner'));
      if (plan.topRuns.length) parts.push('the top');
      if (plan.baseRuns.length) parts.push('the base');
      var what = parts.length > 1 ? parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1] : parts[0];
      lines.push({ id: 'edging', group: 'Trims', callout: 3, name: 'Edging trim ' + Tm + ', ' + trimColour, part: { k: 'edging', L: T, c: trimColour },
        qty: trims.edgingN, unit: P.edging[T], reason: 'Caps ' + what + ': ' + m(trims.edgingLm) + ' in ' + Tm + ' lengths' });
    }
    if (trims.fN && !trims.fUnavailable) {
      var nWin = 0, nDoor = 0;
      job.walls.forEach(function (w) { (w.openings || []).forEach(function (o) { if (o.type === 'door') nDoor++; else nWin++; }); });
      var round = [];
      if (nDoor) round.push(plural(nDoor, 'door')); if (nWin) round.push(plural(nWin, 'window'));
      var fWhat = round.length ? 'Round ' + round.join(' and ') : 'Takes the cut board ends';
      lines.push({ id: 'ftrim', group: 'Trims', callout: 4, name: 'F trim 3.6 m, ' + trimColour, part: { k: 'ftrim', L: 3600, c: trimColour },
        qty: trims.fN, unit: P.ftrim[3600], reason: fWhat + ': ' + m(trims.fLm) + ', mitred at the corners' });
    }
    // Battens
    if (battens) {
      var bn = bat.note, bits = [];
      bits.push(dir === 'vertical' ? plural(bn.rows, 'row') + ' at 500 mm' : plural(bn.rows, 'upright') + ' at 500 mm');
      if (bn.edges) bits.push(plural(bn.edges, 'edge batten'));
      if (bn.joins) bits.push(plural(bn.joins, 'double batten') + ' at joins');
      if (bn.openings) bits.push('framing round openings');
      if (bn.buildouts) bits.push(plural(bn.buildouts, 'corner batten'));
      lines.push({ id: 'battens', group: 'Battens', callout: 5, name: 'Composite batten ' + (battens.B === 3000 ? '3.0 m' : '3.6 m'), part: { k: 'batten', L: battens.B, c: '-' },
        qty: battens.count, unit: battens.unit,
        reason: bits.join(', ') + ': ' + m(bat.lm) + '; ' + (battens.B === 3000 ? '3 m' : '3.6 m') + ' lengths cost less' });
    }
    // Fixings (screws only)
    if (P.screwPack != null) lines.push({ id: 'screws', group: 'Fixings', callout: 6, name: 'Screw pack (100)', qty: screwPacks, unit: P.screwPack, part: { k: 'screws', L: 0, c: '-' },
      reason: 'One pack for every 10 boards fitted' });
    else missing.push('screw packs');
    if (P.colourScrewPack != null) lines.push({ id: 'colourscrews', group: 'Fixings', callout: 7, name: 'Colour-coded screw pack (50), ' + trimColour, part: { k: 'cscrews', L: 0, c: trimColour },
      qty: cs.packs, unit: P.colourScrewPack,
      reason: cs.total + ' screws for the first and last boards and the trims' + (range === 'duo' ? '; Duo Tone always takes black' : '') });
    else missing.push('colour-coded screws in ' + trimColour);

    lines.forEach(function (l) { l.total = l.qty * l.unit; });
    var t = totals(lines, D);

    // Board length reason
    var lenWhy;
    if (range === 'fire') lenWhy = '3.6 m boards: Fire Rated comes in 3.6 m only.';
    else if (alt) {
      var maxH = Math.max.apply(null, job.walls.map(hMax));
      var head = Lm + ' boards: ' + plural(boards.fitted, 'board') + ', ' + money(boards.costFitted) + ' before spares.';
      var other = ' ' + (alt.L === 2500 ? '2.5 m' : '3.6 m') + ' boards would need ' + alt.fitted + ', ' + money(alt.costFitted) + '.';
      if (dir === 'vertical' && maxH <= boards.L) head = Lm + ' boards: one piece per column, no joins, ' + m(boards.L - maxH) + ' cut off each.';
      var minH = Math.min.apply(null, job.walls.map(function (w) { return Math.min(w.h, w.h2 || w.h); }));
      if (dir === 'vertical' && maxH > boards.L) head = Lm + ' boards, with one join ' + (minH > boards.L ? 'in each column' : 'in each column taller than ' + Lm) + ', on a double batten.';
      if (dir === 'horizontal' && boards.maxJoins === 0) head = Lm + ' boards: one board per row, no joins.';
      if (dir === 'horizontal' && boards.maxJoins > 0) head = Lm + ' boards: ' + plural(boards.maxJoins, 'join') + ' in a row at most, staggered on double battens.';
      lenWhy = head + other;
    } else if (choice.dropped && choice.dropped.length) {
      lenWhy = Lm + ' boards, with one join in each column on a double batten: 2.5 m boards would need two joins in a column.';
    } else lenWhy = '';

    var fixings = Math.ceil(bat.lm / BAY);

    return {
      valid: true, dir: dir, range: range, rangeName: rangeName, colour: job.colour, trimColour: trimColour,
      boards: boards, alt: alt, lenWhy: lenWhy, plan: plan, trims: trims, battenPlan: bat, battens: battens,
      screwPacks: screwPacks, colourScrews: cs, area: area, wallFixings: fixings,
      lines: lines, totals: t, perM2: area > 0 ? Math.round(t.total / area) : 0, missing: missing
    };
  }

  return {
    COVER: COVER, BAY: BAY, MIN_PIECE: MIN_PIECE, SPARES_PCT: SPARES_PCT, boardLengths: boardLengths,
    ffd: ffd, splitOptions: splitOptions, packRuns: packRuns, placeOpenings: placeOpenings,
    wallStrips: wallStrips, countBoards: countBoards, chooseBoards: chooseBoards, sparesFor: sparesFor,
    planTrims: planTrims, chooseTrims: chooseTrims, battenLines: battenLines, chooseBattens: chooseBattens,
    colourScrews: colourScrews, totals: totals, quote: quote, netArea: netArea, heightAt: heightAt,
    hMax: hMax, money: money, m: m, plural: plural, sideOf: sideOf, jointCount: jointCount,
    hasCorner: hasCorner, singleL: singleL
  };
})();
if (typeof module !== 'undefined') module.exports = CladdingMaths;

